/* Subscription messages and Online messages pages (sidebar Tools; CONTEXT.md). One
   Account at a time, picked at the top, each showing one half of that Account's own
   Automated messages tab: New subscribers or Online. Same ?account= and last-used
   Account as the Mass messages pages. The tab's components read no per-Account
   permissions (the backend checks automated_messages_setup), so unlike the mass pages
   nothing loads on a switch. */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAccounts } from '../context/AccountsContext';
import { useToolAccess } from '../hooks/useToolAccess';
import { AccountPicker } from '../components/AccountPicker';
import { PageHeading } from '../components/PageHeader';
import { LoadingBlock } from '../components/Spinner';
import { AutomatedMessagesTab } from '../components/AutomatedMessages/AutomatedMessagesTab';
import { accountName } from '../dashboard/attention';
import { chooseAccount, offeredAccounts, readLastAccount, rememberAccount } from './pickAccount';
// The boxed Account bar, as on Dynamic lists and Tracking links.
import '../dynamicLists/dynamicLists.css';

export type AutomatedPageKind = 'subscription' | 'online';

/** The old /automated-messages link: ?asub=online opens Online messages, anything else
 *  Subscription messages; the rest of the query (?account=) is kept. */
export function AutomatedMessagesRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const to = params.get('asub') === 'online' ? '/online-messages' : '/subscription-messages';
  params.delete('asub');
  const rest = params.toString();
  return <Navigate to={rest ? `${to}?${rest}` : to} replace />;
}

export function AutomatedMessagesToolPage({ kind }: { kind: AutomatedPageKind }) {
  const { t } = useTranslation('common');
  const tools = useToolAccess();
  const { accounts, loaded } = useAccounts();
  const [params, setParams] = useSearchParams();

  const offered = useMemo(() => {
    if (!tools || !loaded) return null;
    return offeredAccounts(accounts, tools.mass_pages?.automated_messages)
      .map((a) => ({ account_id: a.account_id, name: accountName(a), username: a.username, platform: a.platform }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tools, loaded, accounts]);

  const fromUrl = params.get('account');
  const accountId = offered ? chooseAccount(offered.map((a) => a.account_id), fromUrl, readLastAccount()) : undefined;

  // Keep the URL on the Account shown (a bare open, or one no longer offered).
  useEffect(() => {
    if (!accountId || accountId === fromUrl) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', accountId);
      return next;
    }, { replace: true });
  }, [accountId, fromUrl, setParams]);

  useEffect(() => {
    if (accountId) rememberAccount(accountId);
  }, [accountId]);

  const choose = (id: string | undefined) => {
    if (!id) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', id);
      return next;
    });
  };

  const body = () => {
    if (!offered) return <LoadingBlock />;
    if (!accountId) return <p style={{ margin: '24px 0', color: 'var(--om-muted)' }}>{t('massPages.automated.none')}</p>;
    return <AutomatedMessagesTab key={accountId} accountId={accountId} only={kind === 'subscription' ? 'setup' : 'online'} />;
  };

  // Welcome messages only reach new subscribers when OnlyFans auto-subscribes the
  // Account back to them; Fansly has no such setting.
  const platform = offered?.find((a) => a.account_id === accountId)?.platform;
  const autoSubscribeWarning = kind === 'subscription' && !!accountId && platform !== 'fansly';

  return (
    <div className="mobile-page-shell cpt-root dl-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading title={t(`massPages.${kind}.title`)} subtitle={t(`massPages.${kind}.subtitle`)} />
      {offered && offered.length > 0 && (
        <div className="dl-bar">
          <div className="dl-bar-account">
            <span className="dl-label">{t('massPages.account')}</span>
            <AccountPicker accounts={offered} value={accountId} onChange={choose} align="left" required />
          </div>
        </div>
      )}
      {autoSubscribeWarning && (
        <p className="dl-notice dl-notice--warn" role="note">
          {t('massPages.subscription.autoSubscribeWarning')}
        </p>
      )}
      {body()}
    </div>
  );
}
